import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function HrEmployeeSearch({
  employees,
  value,
  onChange,
  loading = false,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => employees.find((emp) => String(emp.id) === String(value)),
    [employees, value]
  );

  useEffect(() => {
    if (selected) {
      setQuery(selected.name);
    } else if (!value) {
      setQuery('');
    }
  }, [selected, value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees.slice(0, 15);
    return employees
      .filter(
        (emp) =>
          emp.name?.toLowerCase().includes(q) ||
          emp.department?.toLowerCase().includes(q) ||
          emp.department_name?.toLowerCase().includes(q) ||
          emp.work_email?.toLowerCase().includes(q) ||
          emp.job_title?.toLowerCase().includes(q)
      )
      .slice(0, 15);
  }, [employees, query]);

  const handleInputChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);
    if (!next.trim()) {
      onChange('');
    } else if (selected && next !== selected.name) {
      onChange('');
    }
  };

  const pickEmployee = (emp) => {
    onChange(String(emp.id));
    setQuery(emp.name);
    setOpen(false);
  };

  const clearSelection = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="ea-hr-emp-search" ref={wrapRef}>
      <div className="ea-hr-emp-search__field">
        <input
          type="search"
          className="ea-field-input ea-hr-emp-search__input"
          placeholder={loading ? 'Loading employees…' : 'Search employee by name…'}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          disabled={disabled || loading}
          autoComplete="off"
          aria-label="Search employee"
          aria-expanded={open}
          aria-haspopup="listbox"
        />
        {value && (
          <button
            type="button"
            className="ea-hr-emp-search__clear"
            onClick={clearSelection}
            aria-label="Clear employee selection"
          >
            ×
          </button>
        )}
      </div>
      {open && !loading && suggestions.length > 0 && (
        <ul className="ea-hr-emp-search__list" role="listbox">
          {suggestions.map((emp) => (
            <li key={emp.id} role="option">
              <button
                type="button"
                className={`ea-hr-emp-search__option${
                  String(emp.id) === String(value) ? ' selected' : ''
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickEmployee(emp)}
              >
                <span className="ea-hr-emp-search__name">{emp.name}</span>
                {(emp.department || emp.department_name || emp.job_title) && (
                  <span className="ea-hr-emp-search__meta">
                    {[emp.department || emp.department_name, emp.job_title]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && query.trim() && suggestions.length === 0 && (
        <p className="ea-hr-emp-search__empty">No employees match your search.</p>
      )}
    </div>
  );
}
